import { Component, OnInit, Input, OnChanges, Output } from '@angular/core';
import { User } from '../../services/auth.service';
import { TeamsService } from '../../services/teams.service';
import { Team } from '../../models/team.model';
import { TranslateService } from '@ngx-translate/core';
import { ToastController, Platform } from '@ionic/angular';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import { Member } from '../../models/member.model';
import { Recipient } from '../../models/recipient.model';
import { GSDPhoneValidationCodes } from '../gsd-intl-tel-input/gsd-intl-tel-input-country';
import { EventEmitter } from '@angular/core';
import { AnalyticsService } from '../../services/analytics.service';
import ContactsX from 'cordova-plugin-contacts-x';
import { ContactX } from 'cordova-plugin-contacts-x';
import { IapService } from '../../services/iap.service';

@Component({
  selector: 'add-member-form',
  templateUrl: './add-member-form.component.html',
  styleUrls: ['./add-member-form.component.scss'],
})
export class AddMemberFormComponent implements OnInit, OnChanges {
  @Input() memberType: string;
  @Input() team: Team;
  @Input() teamId: string;
  @Input() action: string;
  @Input() op: string;
  @Input() member: Member;
  @Input() userData: User;

  @Output() public dismissed = new EventEmitter();

  memberForm: FormGroup;
  memberPhone: any;

  pickedContact: Member;
  contactList = [];
  onDevice = false;

  constructor(
    private analytics: AnalyticsService,
    public toastController: ToastController,
    public translate: TranslateService,
    private teamsService: TeamsService,
    private fb: FormBuilder,
    public plt: Platform,
    public iap: IapService,
  ) {}

  ngOnInit() {
    this.plt.ready().then(() => {
      if (this.iap.isMobileApp()) {
        this.onDevice = true;
      }
    });
    if (this.action) {
      this.setFormValues(this.member);
    } else {
      this.setFormValues(this.teamsService.memberDefault());
    }
  }

  ngOnChanges(changes) {
    console.log(changes);
    if (changes.op && changes.op.currentValue === 'save') {
      this.op = 'edit';
      this.memberUpdate();
    }
  }

  setFormValues(member) {
    // memberPhone is used by the gsd-intl-tel-input component (validator equivalents are in the component)
    this.memberPhone = member.mobileNumber;

    this.memberForm = this.fb.group({
      firstName: [member.firstName, Validators.required],
      lastName: [member.lastName, Validators.required],
    });
  }

  async memberUpdate() {
    if (this.memberForm.get('firstName').hasError('required')) {
      this.analytics.track('qt_input_required', {
        source: 'add-member-form.memberUpdate',
        input: 'firstName',
      });
      this.required('INVITES.ERRORS.NAME_REQUIRED');
      return;
    }
    if (this.memberForm.get('lastName').hasError('required')) {
      this.analytics.track('qt_input_required', {
        source: 'add-member-form.memberUpdate',
        input: 'lastName',
      });
      this.required('INVITES.ERRORS.NAME_REQUIRED');
      return;
    }

    if (this.memberPhone.valid === GSDPhoneValidationCodes.REQUIRED) {
      this.analytics.track('qt_input_required', {
        source: 'add-member-form.memberUpdate',
        input: 'mobile.GSDPhoneValidationCodes',
      });
      this.required('INVITES.ERRORS.PHONE_REQUIRED');
      return;
    }

    if (this.memberPhone.valid === GSDPhoneValidationCodes.INVALID) {
      this.analytics.track('qt_input_invalid', {
        source: 'add-member-form.memberUpdate',
        input: 'mobile.GSDPhoneValidationCodes',
      });
      this.required('INVITES.ERRORS.PHONE_INVALID');
      return;
    }

    delete this.memberPhone.valid; // Validation passed and we don't need to pass this to server

    const member = this.memberForm.value;
    member.teamId = this.teamId;
    // Force country, not included in form values by default because its disabled
    // member.mobileNumber.country = '1';
    member.mobileNumber = this.memberPhone;

    if (this.memberType === 'members') {
      member.memberStatus = 'reinvited';
      this.teamsService.memberUpdate(this.member, member);
    } else {
      this.teamsService.staffUpdate(this.member, member);
    }
    this.dismissed.emit(this.memberType);
  }

  async memberAdd() {
    if (this.memberForm.get('firstName').hasError('required')) {
      this.analytics.track('qt_input_required', {
        source: 'add-member-form.memberAdd',
        input: 'firstName',
      });
      this.required('INVITES.ERRORS.NAME_REQUIRED');
      return;
    }
    if (this.memberForm.get('lastName').hasError('required')) {
      this.analytics.track('qt_input_required', {
        source: 'add-member-form.memberAdd',
        input: 'lastName',
      });
      this.required('INVITES.ERRORS.NAME_REQUIRED');
      return;
    }

    if (this.teamsService.isMember(this.memberType, this.memberPhone)) {
      if (this.memberType === 'members') {
        this.analytics.track('qt_member_exists', {
          source: 'add-member-form.memberAdd',
        });
        this.required('INVITES.ERRORS.IS_MEMBER');
        return;
      } else {
        this.analytics.track('qt_staff_exists', {
          source: 'add-member-form.memberAdd',
        });
        this.required('INVITES.ERRORS.IS_STAFF');
        return;
      }
    }

    if (typeof this.memberPhone.valid === 'undefined') {
      this.analytics.track('qt_input_required', {
        source: 'add-member-form.memberAdd',
        input: 'mobile',
      });
      this.required('INVITES.ERRORS.PHONE_REQUIRED');
      return;
    }

    if (this.memberPhone.valid === GSDPhoneValidationCodes.REQUIRED) {
      this.analytics.track('qt_input_invalid', {
        source: 'add-member-form.memberAdd',
        input: 'mobile.GSDPhoneValidationCodes',
      });
      this.required('INVITES.ERRORS.PHONE_REQUIRED');
      return;
    }

    if (this.memberPhone.valid === GSDPhoneValidationCodes.INVALID) {
      this.analytics.track('qt_input_invalid', {
        source: 'add-member-form.memberAdd',
        input: 'mobile.GSDPhoneValidationCodes',
      });
      this.required('INVITES.ERRORS.PHONE_INVALID');
      return;
    }

    delete this.memberPhone.valid; // Validation passed and we don't need to pass this to server

    const member = this.memberForm.value;
    member.teamId = this.teamId;
    // Force country, not included in form values by default because its disabled
    // member.mobileNumber.country = '1';
    member.mobileNumber = this.memberPhone;
    member.createdByName = `${this.userData.firstName} ${this.userData.lastName}`;

    if (this.memberType === 'members') {
      // Add new member
      const memberDoc = await this.teamsService.memberAdd(member);

      // Add member to the active message
      if (typeof this.team.messagesStatus?.latest === 'string') {
        const recipient: Recipient = {
          memberId: memberDoc.id,
          latestReply: null,
        };

        this.teamsService.messageRecipientAdd(
          this.team.messagesStatus.latest,
          recipient,
        );
      }
    } else {
      this.teamsService.staffAdd(member);
    }

    this.setFormValues(this.teamsService.memberDefault());
  }

  async required(errMsg: string) {
    const toast = await this.toastController.create({
      message: this.translate.instant(errMsg),
      duration: 2000,
      color: 'danger',
    });
    toast.present();
  }

  gsdPhone(phoneData) {
    console.log('gsdPhone: ', phoneData);
    this.memberPhone = phoneData;
  }

  pickContactOld() {
    ContactsX.pick(
      function (picked: ContactX) {
        console.log('Picked Contact', picked);
        const contact: Member = this.teamsService.memberDefault();
        console.log(picked);
        this.analytics.track('qt_contact_picked', {
          source: 'invite-members-page',
          ui: true,
        });
        contact.firstName = picked.firstName;
        contact.lastName = picked.familyName;
        if (picked.phoneNumbers) {
          // TODO: Allow picking a specific phone number
          contact.mobileNumber.full = `+1 ${picked.phoneNumbers[0].value}`;
        } else {
          this.analytics.track('qt_contact_numberless', {
            source: 'invite-members-page',
          });
          console.log(
            `${picked.firstName} ${picked.familyName} has no phone numbers`,
          );
        }
        this.setFormValues(contact);
        this.pickedContact = contact;
        this.contactList.push(contact);
      },
      async function (error) {
        console.error('Pick Contact Error', error);
        const errMsg = 'An error occurred picking a contact.';
        const toast = await this.toastController.create({
          message: this.translate.instant(errMsg),
          duration: 2000,
          color: 'danger',
        });
        toast.present();
      },
    );
  }

  onAddContact() {
    if (this.iap.isMobileApp()) {
      let __this = this;
      ContactsX.hasPermission(
        function (success) {
          console.log('Permissions:', success);
          if (success.read) {
            __this.pickContact();
          } else {
            __this.askPermissions();
          }
        },
        function (error) {
          console.error('Permissions Error:', error);
        },
      );
    } else {
      console.log('You must be on a device to use your contacts.');
    }
  }

  askPermissions() {
    const __this = this;
    ContactsX.requestPermission(
      async function (success) {
        if (success.read) {
          __this.pickContact();
        } else {
          const errMsg =
            'QuickText needs permission to access your Contacts. You can fix this in your device Settings at Privacy/Contacts.';
          const toast = await this.toastController.create({
            message: this.translate.instant(errMsg),
            duration: 2000,
            color: 'danger',
          });
          toast.present();
        }
      },
      function (error) {
        console.error('Contacts error', error);
      },
    );
  }

  pickContact() {
    const __this = this;
    ContactsX.pick(
      async function (picked: ContactX) {
        console.log('Picked Contact', picked);
        const contact: Member = __this.teamsService.memberDefault();
        console.log(picked);
        __this.analytics.track('qt_contact_picked', {
          source: 'add-member-form',
          ui: true,
        });
        contact.firstName = picked.firstName;
        contact.lastName = picked.familyName;
        if (picked.phoneNumbers) {
          // TODO: Allow picking a specific phone number
          const friendly = `+1 ${picked.phoneNumbers[0].value}`;
          let split = friendly
            .replace(/[(]|[)]/gm, '')
            .replace('-', ' ')
            .split(' ');
          contact.mobileNumber = {
            country: `+1`,
            area: split[1],
            prefix: split[2],
            line: split[3],
            full: `+1${split[1]}${split[2]}${split[3]}`,
            friendly: friendly,
            countryTag: 'us',
          };
          __this.memberPhone = contact.mobileNumber;
          console.log('pre-picked contact', __this.member);
          __this.member = contact;
          __this.setFormValues(__this.member);
          console.log('picked contact', __this.member);
        } else {
          __this.analytics.track('qt_contact_numberless', {
            source: 'add-member-form',
          });
          const msg = `${picked.firstName} ${picked.familyName} has no phone numbers`;
          console.log(msg);
          const toast = await __this.toastController.create({
            message: __this.translate.instant(msg),
            duration: 2000,
            color: 'danger',
          });
          toast.present();
        }

        __this.setFormValues(contact);
        __this.pickedContact = contact;
        __this.contactList.push(contact);
      },
      async function (error) {
        console.error('Pick Contact Error', error);
        __this.analytics.track('qt_contact_error', {
          source: 'add-member-form',
          error: JSON.stringify(error),
        });
        const errMsg =
          'An unknown error occurred picking a contact. Please try again later.';
        const toast = await __this.toastController.create({
          message: errMsg,
          duration: 2000,
          color: 'danger',
        });
        toast.present();
      },
    );
  }
}
